import { useMemo } from 'react';
import { DataTable, EmptyState, Icon, Panel, Stat, type Column } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { GrantCell } from './columns.tsx';
import type { MatrixPayload, PermissionDef, RoleDef } from './types.ts';

/**
 * Tab 2 — the capability matrix, and it is a CONTROL now.
 *
 * "Click a cell to grant or remove — every change is audit-logged" is the
 * design's hint, and it was not true on this build: the grid was rendered from
 * the ROLE_MATRIX constant in @dayon/rbac, so no click could have changed what
 * any guard enforced and the honest thing was to say so.
 *
 * What was actually missing was smaller than it looked. The GUARDS already read
 * identity.role_permission — `loadPrincipal` selects a principal's capabilities
 * from that table on every single request — so the table was always the source
 * of truth and the SCREEN was the copy. It now reads the same table, a cell is
 * a real grant or revocation, and the change binds at the holder's next request
 * because nothing is baked into a session token.
 *
 * Three kinds of cell, and they are three different facts:
 *   · a toggle — press it, give a reason, and the row moves;
 *   · a PADLOCK — access.manage on a super role. The route asks
 *     refuseRoleChange() and the platform's last-administrator rule, and a
 *     database trigger refuses the state outright; the console does not offer
 *     the cell, because this is not a decision an operator should be talked out
 *     of at a confirmation dialog;
 *   · a CLOSED cell — a write capability on a read-only role. The route refuses
 *     it ("an Auditor holds no write capability" is what the role is FOR), so
 *     the grid says so on the cell rather than in a modal after the click.
 */
export function PermissionMatrix({
  matrix,
  loading,
  failed,
  twoFactorRoles,
  onToggle,
}: {
  matrix: MatrixPayload | null;
  loading: boolean;
  failed: boolean;
  twoFactorRoles: Set<string>;
  onToggle: (role: RoleDef, permission: PermissionDef, grant: boolean) => void;
}) {
  const columns = useMemo<Column<PermissionDef>[]>(() => {
    const roles = matrix?.roles ?? [];
    const readOnly = new Set(matrix?.readOnlyRoles ?? []);

    const capability: Column<PermissionDef> = {
      key: 'capability',
      header: 'Capability',
      render: (p) => (
        <>
          {p.label}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {p.key}
            {p.requiresTwoFactor ? ' · 2FA required' : ''}
            {p.writes ? ' · writes' : ''}
          </div>
        </>
      ),
    };

    return [
      capability,
      ...roles.map<Column<PermissionDef>>((r) => ({
        key: r.name,
        header: r.name,
        render: (p) => {
          const granted = r.permissions.includes(p.key);
          /* is_super comes from identity.role now rather than being inferred
             from which admins happen to hold the role — so a super role with
             no admins on it keeps its padlock. */
          const locked = p.key === 'access.manage' && r.isSuper;
          const closed =
            !granted && readOnly.has(r.name) && p.writes
              ? `${r.name} is read-only and ${p.label} changes something`
              : null;
          return (
            <GrantCell
              granted={granted}
              locked={locked}
              closed={closed}
              role={r.name}
              capability={p.label}
              onToggle={locked || closed ? null : () => onToggle(r, p, !granted)}
            />
          );
        },
      })),
    ];
  }, [matrix, onToggle]);

  const edited = (matrix?.roles ?? []).filter((r) => !r.systemManaged);

  /*
   * WHY NONE OF THE PROSE BELOW NAMES WHERE ANY OF THIS LIVES.
   *
   * It used to. The notice opened "This grid is identity.role_permission,
   * which is the table every server-side guard reads", the failed-read empty
   * state said the grants were "unchanged in identity.role_permission", and
   * both of them named /api/access/matrix as the thing that had not answered.
   * An Access Steward deciding whether to trust a blank grid can do nothing
   * with either name: it is not something they can look up, ask anybody for,
   * or repeat to support, and the sentence stops being about them the moment it
   * appears.
   *
   * THE CLAIMS ARE THE REASON THOSE SENTENCES EXIST, and every one of them is
   * kept here in meaning even though the identifiers are gone:
   *
   *   · the grid is the single record every permission check reads —
   *     `loadPrincipal` in the session plugin re-selects a principal's
   *     capabilities from identity.role_permission on every single request;
   *   · therefore a cell is a real grant or revocation rather than a picture of
   *     one, written in the same transaction as its audit row so neither half
   *     can land without the other;
   *   · therefore it binds at the holder's NEXT REQUEST and not at their next
   *     sign-in, because nothing about capabilities is baked into the session
   *     token;
   *   · a revocation sets identity.role_permission.revoked_at rather than
   *     deleting the row — which the application's database role could not do
   *     in any case, holding DELETE on no table in this schema — and the
   *     history of when a role gained and lost a capability is worth keeping;
   *   · a refused GET /api/access/matrix leaves every existing grant in force
   *     exactly as it was, enforced from the same place, because the read that
   *     failed was the console's and not any guard's.
   *
   * That last one is the distinction most worth protecting. "Nothing was read"
   * and "there is genuinely nothing here" are the same blank grid and two
   * completely different facts, and the second would be a platform fault worth
   * waking somebody for: the permission catalogue is compiled into the software
   * itself, so it cannot legitimately come back empty.
   */
  return (
    <>
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          This grid is the <strong>single record of who may do what</strong>, and every
          permission check in the platform reads it: each time an admin asks the platform for
          anything, what they are allowed to do is looked up here afresh. So a cell is a real
          grant or removal, not a picture of one — the change and the audit entry naming who
          made it are saved together, so neither can exist without the other — and it takes
          hold on that person&rsquo;s <strong>next action</strong> rather than at their next
          sign-in: nobody is signed out and nobody has to sign in again. Taking a capability
          away marks it withdrawn on the date it happened instead of erasing it, because the
          history of when a role gained and lost a capability is worth keeping; nothing in
          this console is able to erase it.
        </div>
      </div>

      {edited.length > 0 && (
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            {/* `system_managed = false`: an operator changed a grant since `pnpm db:seed` wrote
                the role from the ROLE_MATRIX constant. The seed truncates and re-inserts, naming
                these roles first; migrations do not run it. The seed refuses NODE_ENV=production
                (packages/db/src/seed.ts) and staging's first deploy runs it once
                (deploy/deploy.sh, "Seed"). */}
            {edited.map((r) => r.name).join(', ')}{' '}
            {edited.length === 1 ? 'has' : 'have'} been edited from this console, so{' '}
            {edited.length === 1 ? 'it' : 'they'} may no longer match the platform&rsquo;s
            built-in definition. Resetting an installation to its starting data puts this grid back
            to that definition and throws away the edits made here, naming these roles before it
            does. The platform refuses to do that to a production installation; a test
            installation is reset this way when it is first set up, and on a developer&rsquo;s
            machine whenever they choose.
          </div>
        </div>
      )}

      {failed ? (
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            The per-role summary is withheld: the capability list could not be read, so there is
            nothing to count each role against. A row of zeros here would say every role holds
            nothing.
          </div>
        </div>
      ) : loading && !matrix ? (
        <div className="skeleton" style={{ height: '90px' }}>
          &nbsp;
        </div>
      ) : (
        <div className="stat-grid">
          {(matrix?.roles ?? []).map((r) => (
            <Stat
              key={r.name}
              label={r.name}
              value={formatCount(r.adminCount)}
              delta={`${formatCount(r.permissions.length)} of ${formatCount(matrix?.permissions.length ?? 0)} capabilities${twoFactorRoles.has(r.name) ? ' · 2FA required' : ''}`}
            />
          ))}
        </div>
      )}

      <Panel title="Role & permission matrix">
        {loading && !matrix ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          /* One column per role, so the grid grows with them and scrolls inside
             its own panel rather than making the page scroll sideways. */
          <div className="matrix-scroll">
            <DataTable
              caption="Capabilities down the side, roles across the top; press a cell to grant or take away"
              columns={columns}
              rows={matrix?.permissions ?? []}
              rowKey={(p) => p.key}
              empty={
                failed ? (
                  // "It answered with nothing" and "it did not answer" are the
                  // same blank grid and two different faults.
                  <EmptyState icon="warning-circle" title="The matrix could not be read">
                    The platform did not answer when this screen asked it for the capability
                    list, so no cell below was ever drawn. This says nothing about who holds
                    what: every grant is exactly as it was, and every permission check in the
                    platform is still being made against them. Try again shortly.
                  </EmptyState>
                ) : (
                  <EmptyState icon="sliders" title="No capabilities returned">
                    The platform answered with an empty permission list. That is a fault, not an
                    empty configuration — the full catalogue of permissions is built into the
                    software itself, so it cannot legitimately be empty.
                  </EmptyState>
                )
              }
            />
          </div>
        )}
      </Panel>
    </>
  );
}
